import { ChecklistActions, ChecklistActionTypes } from '../actions';
import { photoStepIncrement } from '@shared/*';
import { ChecklistInbound, InProgressChecklist } from '@models/*';

export interface ChecklistState {
    loadedChecklists: { [transportId: string]: ChecklistInbound };
    inProgressChecklists: { [transportId: string]: InProgressChecklist };
    checklistsLoading: number[];
    crosschecksLoading: number[];
    checklistPhotoGallerySteps: { [transportId: string]: { photos: number; damagePhotos: number } };
    protocolPhotosInTransit: { [transportId: string]: { totalPhotos: number, currPhotos: number } };
    protocolPhotosInTransitInit: boolean;
    selectedContactOnSite: string | null;
    bulkSignList: number[];
    saving: boolean;
    bulkSignLoading: boolean;
}

// todo add processedCrosscheck instead of any

const initState: ChecklistState = {
    loadedChecklists: {},
    inProgressChecklists: {},
    checklistsLoading: [],
    crosschecksLoading: [],
    checklistPhotoGallerySteps: {},
    protocolPhotosInTransit: {},
    protocolPhotosInTransitInit: false,
    selectedContactOnSite: null,
    bulkSignList: [],
    saving: false,
    bulkSignLoading: false
};

export function checklistReducer(
    state = initState,
    action: ChecklistActions
): ChecklistState {
    switch (action.type) {
        case ChecklistActionTypes.LOAD_CHECKLIST: {
            return {
                ...state,
                checklistsLoading: [...state.checklistsLoading, action.payload.tourStopActionId]
            };
        }

        case ChecklistActionTypes.LOAD_CHECKLIST_FAIL: {
            return {
                ...state,
                checklistsLoading: [...state.checklistsLoading.filter(id => id !== action.payload.tourStopActionId)]
            };
        }

        case ChecklistActionTypes.LOAD_CHECKLIST_SUCCESS: {
            const loadedChecklists = {
                ...state.loadedChecklists,
                [action.payload.checklist.transportInternalId]: action.payload.checklist
            };

            return {
                ...state,
                checklistsLoading: [...state.checklistsLoading.filter(id => id !== action.payload.tourStopActionId)],
                loadedChecklists
            };
        }

        case ChecklistActionTypes.LOAD_CROSSCHECK: {
            return {
                ...state,
                crosschecksLoading: [...state.crosschecksLoading, action.payload.tourStopActionId]
            };
        }

        case ChecklistActionTypes.LOAD_CROSSCHECK_FAIL: {
            return {
                ...state,
                crosschecksLoading: [...state.crosschecksLoading.filter(id => id !== action.payload.tourStopActionId)]
            };
        }

        case ChecklistActionTypes.LOAD_CROSSCHECK_SUCCESS: {
            const loadedChecklists = {
                ...state.loadedChecklists,
                [action.payload.checklist.transportInternalId]: action.payload.checklist
            };

            return {
                ...state,
                crosschecksLoading: [...state.crosschecksLoading.filter(id => id !== action.payload.tourStopActionId)],
                loadedChecklists
            };
        }

        case ChecklistActionTypes.SAVE_CHECKLIST_PROGRESS: {
            const inProgressChecklists = {
                ...state.inProgressChecklists,
                [action.payload.transportInternalId]: action.payload
            };

            return {
                ...state,
                inProgressChecklists
            };
        }

        case ChecklistActionTypes.DELETE_CHECKLIST_PROGRESS: {
            const { [action.payload]: deletedEntity, ...inProgressChecklists } = state.inProgressChecklists;

            return {
                ...state,
                inProgressChecklists
            };
        }

        case ChecklistActionTypes.INCREASE_PHOTO_STEP: {
            const transportId = action.payload.transportId;
            let newGalleryStep;

            if (!state.checklistPhotoGallerySteps[transportId]) {
                newGalleryStep = {
                    photos: photoStepIncrement,
                    damagePhotos: photoStepIncrement
                };
                newGalleryStep[action.payload.type] += photoStepIncrement;
            } else {
                newGalleryStep = {
                    ...state.checklistPhotoGallerySteps[transportId],
                    [action.payload.type]: state.checklistPhotoGallerySteps[transportId][action.payload.type] + photoStepIncrement
                };
            }

            const checklistPhotoGallerySteps = {
                ...state.checklistPhotoGallerySteps,
                [transportId]: newGalleryStep
            };

            return {
                ...state,
                checklistPhotoGallerySteps
            };
        }

        case ChecklistActionTypes.SAVE_PROTOCOL_DRAFT_SUCCESS:
        case ChecklistActionTypes.SUBMIT_PROTOCOL_SUCCESS: {
            let protocolPhotosInTransit;
            if (action.payload?.photosToUploadLength) {
                protocolPhotosInTransit = {
                    ...state.protocolPhotosInTransit,
                    [action.payload.transportInternalId]: {
                        totalPhotos: action.payload.photosToUploadLength,
                        currPhotos: action.payload.photosToUploadLength,
                    }
                };
            } else {
                protocolPhotosInTransit = state.protocolPhotosInTransit;
            }

            return {
                ...state,
                protocolPhotosInTransit,
                saving: false
            };
        }

        case ChecklistActionTypes.SAVE_PROTOCOL_DRAFT:
        case ChecklistActionTypes.SUBMIT_PROTOCOL: {
          return {...state, saving: true};
        }

        case ChecklistActionTypes.SAVE_PROTOCOL_DRAFT_FAIL:
        case ChecklistActionTypes.SUBMIT_PROTOCOL_FAIL: {
          return {...state, saving: false};
        }

        case ChecklistActionTypes.INIT_PROTOCOL_PHOTOS_IN_TRANSIT: {
            const protocolPhotosInTransit = Object.keys(action.payload).reduce((accum, key) => {
                const currItem = action.payload[key];

                if (currItem.currPhotos) {
                    return { ...accum, [key]: currItem };
                } else {
                    return accum;
                }
            }, {});

            return {
                ...state,
                protocolPhotosInTransit,
                protocolPhotosInTransitInit: true
            };
        }

        case ChecklistActionTypes.PROTOCOL_PHOTO_TRANSIT_SUCCESS: {
            const updatedData = state.protocolPhotosInTransit[action.payload];
            let updatedPhotosInTransit;

            if (updatedData) {
                updatedPhotosInTransit = {
                    ...state.protocolPhotosInTransit,
                    [action.payload]: {
                        totalPhotos: updatedData.totalPhotos,
                        currPhotos: updatedData.currPhotos - 1
                    }
                };
            } else {
                updatedPhotosInTransit = state.protocolPhotosInTransit;
            }

            const protocolPhotosInTransit = Object.keys(updatedPhotosInTransit).reduce((accum, key) => {
                const currItem = updatedPhotosInTransit[key];

                if (currItem.currPhotos) {
                    return { ...accum, [key]: currItem };
                } else {
                    return accum;
                }
            }, {});

            return {
                ...state,
                protocolPhotosInTransit
            };
        }

        case ChecklistActionTypes.ADD_CONTACT_ON_SITE: {
            return {
                ...state,
                selectedContactOnSite: action.payload
            }
        }

        case ChecklistActionTypes.CLEAR_CONTACT_ON_SITE: {
            return {
                ...state,
                selectedContactOnSite: null
            }
        }

        case ChecklistActionTypes.ADD_CHECKLIST_FOR_BULKSIGN: {


            return {
                ...state,
                bulkSignList: [...state.bulkSignList, action.payload]
            }
        }
        case ChecklistActionTypes.REMOVE_CHECKLIST_FOR_BULKSIGN: {
            return {
                ...state,
                bulkSignList: state.bulkSignList.filter( id => id !== action.payload)
            }
        }

        case ChecklistActionTypes.CLEAR_CHECKLISTS_FOR_BULKSIGN: {
            return {
                ...state,
                bulkSignList: []
            }
        }

        case ChecklistActionTypes.SUBMIT_BULK_SIGN_PROTOCOL:
        case ChecklistActionTypes.SUBMIT_BULK_SIGN_PROTOCOL_FAIL: {
            return {
                ...state,
                bulkSignLoading: true
            }
        }

        case ChecklistActionTypes.SUBMIT_BULK_SIGN_PROTOCOL_SUCCESS: {
            return {
                ...state,
                bulkSignList: [],
                bulkSignLoading: false
            }
        }

        case ChecklistActionTypes.SUBMIT_BULK_SIGN_PROTOCOL_FAIL : {

        }
    }

    return state;
}
